import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, Mail, Building2, Briefcase, IndianRupee, Tag, Calendar, FileText, Send, Clock, Sparkles, Users, Trophy, XCircle, Video, Ghost, AlertTriangle, Bell } from "lucide-react";
import { STATUSES, STATUS_COLORS, PRIORITY_COLORS, STATUS_HEX } from "@/lib/constants";
import { slaLevelFor } from "@/lib/sla";
import ScheduleTeamsMeetingDialog from "@/components/ScheduleTeamsMeetingDialog";
import AuditLogPanel from "@/components/AuditLogPanel";
import PingDialog from "@/components/PingDialog";
import { shortDateTime, relativeLabel } from "@/lib/date";

const LIFECYCLE = ["New", "Contacted", "Qualified", "Proposal", "Won"];

export default function LeadDetail({ lead, open, onOpenChange, onChanged, members = [] }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin";
  const canReassign = isAdmin || !!user?.can_reassign;
  const [activities, setActivities] = useState([]);
  const [text, setText] = useState("");
  const [type, setType] = useState("note");
  const [saving, setSaving] = useState(false);
  const [currentLead, setCurrentLead] = useState(lead);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [pingOpen, setPingOpen] = useState(false);

  useEffect(() => { setCurrentLead(lead); }, [lead]);

  useEffect(() => {
    if (open && lead?.id) {
      api.get(`/leads/${lead.id}/activities`).then((r) => setActivities(r.data)).catch(() => {});
    }
  }, [open, lead?.id]);

  if (!currentLead) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/leads/${currentLead.id}/activities`, { type, content: text.trim() });
      setActivities([data, ...activities]);
      setText("");
      toast.success("Activity logged");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const updateStatus = async (newStatus) => {
    try {
      const { data } = await api.patch(`/leads/${currentLead.id}`, { status: newStatus });
      setCurrentLead(data);
      const acts = await api.get(`/leads/${currentLead.id}/activities`);
      setActivities(acts.data);
      onChanged?.();
      toast.success(`Moved to ${newStatus}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const reassign = async (uid) => {
    try {
      await api.post(`/leads/${currentLead.id}/assign`, { assigned_to: uid === "unassigned" ? "" : uid });
      onChanged?.();
      toast.success("Reassigned");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const iconFor = (t) => ({ note: FileText, call: Phone, meeting: Video, email: Mail, status_change: Clock, created: Sparkles }[t] || FileText);
  const iconColorFor = (t) => ({
    note:          { bg: "bg-slate-50 border-slate-200",       fg: "text-slate-600" },
    call:          { bg: "bg-emerald-50 border-emerald-200",   fg: "text-emerald-600" },
    meeting:       { bg: "bg-violet-50 border-violet-200",     fg: "text-violet-600" },
    email:         { bg: "bg-sky-50 border-sky-200",           fg: "text-sky-600" },
    status_change: { bg: "bg-amber-50 border-amber-200",       fg: "text-amber-600" },
    created:       { bg: "bg-indigo-50 border-indigo-200",     fg: "text-indigo-600" },
  }[t] || { bg: "bg-slate-50 border-slate-200", fg: "text-slate-600" });

  const stageIdx = LIFECYCLE.indexOf(currentLead.status);
  const isLost = currentLead.status === "Lost";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-heavy border-slate-200 max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="lead-detail-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-lg font-semibold text-slate-900 truncate">{currentLead.name}</DialogTitle>
              {currentLead.company && <p className="text-slate-600 mt-1 truncate">{currentLead.company}</p>}
              {currentLead.created_at && (
                <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1.5" data-testid="lead-detail-added-on">
                  <Calendar className="w-3 h-3 text-slate-400" />
                  Added {shortDateTime(currentLead.created_at)} · {relativeLabel(currentLead.created_at)}
                </p>
              )}
            </div>
            <div className="flex gap-2 flex-shrink-0 flex-wrap">
              <span className={STATUS_COLORS[currentLead.status]}>{currentLead.status}</span>
              <span className={PRIORITY_COLORS[currentLead.priority]}>{currentLead.priority}</span>
              {(() => {
                const { level, days } = slaLevelFor(currentLead);
                if (level === "ok") return null;
                const cls = level === "critical"
                  ? "bg-rose-50 text-rose-700 border-rose-200"
                  : "bg-amber-50 text-amber-800 border-amber-200";
                const Icon = level === "critical" ? AlertTriangle : Clock;
                return (
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border ${cls}`}
                    data-testid="lead-detail-sla-badge"
                    title={`${days} day${days === 1 ? "" : "s"} in ${currentLead.status}`}
                  >
                    <Icon className="w-3 h-3" /> {days}d in stage
                  </span>
                );
              })()}
              {currentLead.is_stale && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200"
                  data-testid="lead-detail-stale-badge"
                  title="No activity for 14+ days"
                >
                  <Ghost className="w-3 h-3" /> Stale
                </span>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Lifecycle stepper */}
        <div className="glass rounded-2xl p-4 mt-2" data-testid="lifecycle-stepper">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-widest text-slate-500">Lifecycle</p>
            {isLost && (
              <span className="text-xs text-rose-600 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Marked as Lost</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {LIFECYCLE.map((s, i) => {
              const done = !isLost && i <= stageIdx;
              const current = !isLost && i === stageIdx;
              const isWon = s === "Won" && done;
              const stageColor = STATUS_HEX[s] || "#1D63ED";
              return (
                <div key={s} className="flex-1 flex items-center gap-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold border transition-colors
                        ${done ? "text-white" : "bg-white/60 border-white/80 text-slate-400"}
                        ${current ? "ring-2 ring-offset-2 ring-offset-transparent" : ""}
                      `}
                      style={done ? { background: stageColor, borderColor: stageColor } : undefined}
                      data-testid={`lifecycle-step-${s.toLowerCase()}`}
                    >
                      {isWon ? <Trophy className="w-3.5 h-3.5" /> : i + 1}
                    </div>
                    <p className={`text-[10px] mt-1 ${done ? "text-slate-700 font-medium" : "text-slate-400"}`}>{s}</p>
                  </div>
                  {i < LIFECYCLE.length - 1 && (
                    <div
                      className="h-px flex-1 -mt-4"
                      style={i < stageIdx && !isLost ? { background: STATUS_HEX[LIFECYCLE[i + 1]] || "#1D63ED" } : { background: "#cbd5e1" }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Phone card with click-to-call + WhatsApp */}
              <div className="solid-panel p-3">
                <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
                  <Phone className="w-3.5 h-3.5" /> Phone
                </div>
                {currentLead.phone ? (
                  <div className="mt-1 flex items-center gap-2">
                    <a href={`tel:${currentLead.phone}`} className="font-medium text-slate-800 hover:underline" data-testid="lead-tel-link">{currentLead.phone}</a>
                    <a href={`https://wa.me/${(currentLead.phone.match(/\d/g) || []).join("")}`} target="_blank" rel="noreferrer" className="ml-auto text-[11px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100" data-testid="lead-wa-link">
                      WhatsApp
                    </a>
                  </div>
                ) : <p className="mt-1 font-medium text-slate-400">—</p>}
                {(currentLead.phones_extra || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-200 space-y-1" data-testid="lead-phones-extra">
                    {currentLead.phones_extra.map((p, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <a href={`tel:${p}`} className="text-slate-700 hover:underline">{p}</a>
                        <a href={`https://wa.me/${(p.match(/\d/g) || []).join("")}`} target="_blank" rel="noreferrer" className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100">
                          WA
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Email card with mailto */}
              <div className="solid-panel p-3">
                <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
                  <Mail className="w-3.5 h-3.5" /> Email
                </div>
                {currentLead.email ? (
                  <a href={`mailto:${currentLead.email}`} className="mt-1 block font-medium text-slate-800 hover:underline truncate" data-testid="lead-email-link">{currentLead.email}</a>
                ) : <p className="mt-1 font-medium text-slate-400">—</p>}
                {(currentLead.emails_extra || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-200 space-y-1" data-testid="lead-emails-extra">
                    {currentLead.emails_extra.map((em, i) => (
                      <a key={i} href={`mailto:${em}`} className="block text-xs text-slate-700 hover:underline truncate">{em}</a>
                    ))}
                  </div>
                )}
              </div>
              {[
                { icon: Building2, label: "Company", value: currentLead.company || "—" },
                { icon: Briefcase, label: "Designation", value: currentLead.designation || "—" },
                { icon: IndianRupee, label: "Budget", value: currentLead.budget ? `₹${Number(currentLead.budget).toLocaleString()}` : "—" },
                { icon: Calendar, label: "Follow-up", value: currentLead.follow_up_date || "—" },
                { icon: Tag, label: "Source", value: currentLead.source },
              ].map((f) => (
                <div key={f.label} className="solid-panel p-3">
                  <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
                    <f.icon className="w-3.5 h-3.5" /> {f.label}
                  </div>
                  <p className="mt-1 font-medium truncate">{f.value}</p>
                </div>
              ))}
            </div>

            {(currentLead.product || currentLead.setup_cost || currentLead.monthly_cost || currentLead.account_type) && (
              <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-4" data-testid="lead-product-panel">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 rounded-md bg-indigo-100 border border-indigo-200 flex items-center justify-center">
                    <span className="w-2 h-2 rounded-sm bg-indigo-500" />
                  </span>
                  <p className="text-xs uppercase tracking-widest text-indigo-700 font-medium">Product & pricing</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-slate-500">Product</p>
                    <p className="text-sm font-medium text-slate-800 mt-0.5">{currentLead.product || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-slate-500">Account</p>
                    {currentLead.account_type ? (
                      <span className={`inline-flex mt-0.5 items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${currentLead.account_type === "prepaid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                        {currentLead.account_type === "prepaid" ? "Prepaid" : "Postpaid"}
                      </span>
                    ) : <p className="text-sm text-slate-400 mt-0.5">—</p>}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-slate-500">Setup (₹)</p>
                    <p className="text-sm font-medium text-slate-800 mt-0.5">
                      {currentLead.setup_cost ? `₹${Number(currentLead.setup_cost).toLocaleString()}` : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-slate-500">Monthly (₹)</p>
                    <p className="text-sm font-medium text-slate-800 mt-0.5">
                      {currentLead.monthly_cost ? `₹${Number(currentLead.monthly_cost).toLocaleString()}/mo` : "—"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {currentLead.tags?.length > 0 && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-widest mb-2">Tags</p>
                <div className="flex flex-wrap gap-1.5">
                  {currentLead.tags.map((t) => (
                    <span key={t} className="text-xs px-2 py-1 rounded-full bg-slate-100/70 border border-slate-200">{t}</span>
                  ))}
                </div>
              </div>
            )}

            {currentLead.notes && (
              <div className="solid-panel p-4">
                <p className="text-xs text-slate-500 uppercase tracking-widest mb-2">Notes</p>
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{currentLead.notes}</p>
              </div>
            )}

            <form onSubmit={submit} className="solid-panel p-4 space-y-3">
              <p className="text-xs text-slate-500 uppercase tracking-widest">Log Activity</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="w-full sm:w-32 bg-slate-100/70 border-slate-200" data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">Note</SelectItem>
                    <SelectItem value="call">Call</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex-1 flex gap-2">
                  <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="What happened?" className="bg-slate-100/70 border-slate-200 flex-1 min-w-0" data-testid="activity-content-input" />
                  <Button type="submit" disabled={saving || !text.trim()} className="bg-[#1D63ED] hover:bg-[#1854D9] text-white self-end flex-shrink-0" data-testid="activity-submit-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </form>

            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">Activity Timeline</p>
              <div className="space-y-3">
                {activities.length === 0 && <p className="text-sm text-slate-400 italic">No activity yet.</p>}
                {activities.map((a) => {
                  const Icon = iconFor(a.type);
                  const c = iconColorFor(a.type);
                  return (
                    <div key={a.id} className="flex gap-3">
                      <div className={`w-9 h-9 rounded-full border flex items-center justify-center flex-shrink-0 ${c.bg}`}>
                        <Icon className={`w-4 h-4 ${c.fg}`} />
                      </div>
                      <div className="flex-1 solid-panel p-3">
                        <div className="flex justify-between items-baseline mb-1">
                          <p className={`text-xs font-medium capitalize ${c.fg}`}>{a.type.replace("_", " ")}</p>
                          <p className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}</p>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{a.content}</p>
                        <p className="text-xs text-slate-400 mt-1">by {a.user_name}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="solid-panel p-4">
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">Actions</p>
              <Button
                onClick={() => setScheduleOpen(true)}
                className="w-full bg-[#464EB8] hover:bg-[#3C43A0] text-white justify-start"
                data-testid="schedule-teams-btn"
              >
                <Video className="w-4 h-4 mr-2" /> Schedule Teams meeting
              </Button>
              {isAdmin && currentLead.assigned_to && currentLead.assigned_to !== user?.id && (
                <Button
                  onClick={() => setPingOpen(true)}
                  className="w-full mt-2 bg-rose-500 hover:bg-rose-600 text-white justify-start shadow-sm shadow-rose-200"
                  data-testid="ping-member-btn"
                >
                  <Bell className="w-4 h-4 mr-2" /> Ping assignee
                </Button>
              )}
            </div>

            <div className="solid-panel p-4">
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">Change Status</p>
              <Select value={currentLead.status} onValueChange={updateStatus}>
                <SelectTrigger className="bg-slate-100/70 border-slate-200" data-testid="lead-detail-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {canReassign && (
              <div className="solid-panel p-4">
                <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">Assign</p>
                <Select value={currentLead.assigned_to || "unassigned"} onValueChange={reassign}>
                  <SelectTrigger className="bg-slate-100/70 border-slate-200" data-testid="lead-detail-assign-select"><SelectValue placeholder="Assign to" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isAdmin && <AuditLogPanel leadId={currentLead.id} />}
          </div>
        </div>
      </DialogContent>

      <ScheduleTeamsMeetingDialog
        lead={currentLead}
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        onScheduled={async () => {
          try {
            const acts = await api.get(`/leads/${currentLead.id}/activities`);
            setActivities(acts.data);
          } catch { /* ignore */ }
        }}
      />
      <PingDialog
        lead={currentLead}
        open={pingOpen}
        onOpenChange={setPingOpen}
        memberName={members.find((m) => m.id === currentLead.assigned_to)?.name || ""}
        onSent={async () => {
          try {
            const acts = await api.get(`/leads/${currentLead.id}/activities`);
            setActivities(acts.data);
          } catch { /* ignore */ }
        }}
      />
    </Dialog>
  );
}
